import React, { useState, useRef, useEffect } from "react";

// ─── Design tokens ────────────────────────────────────────
const NAVY = "#0C2340", SKY = "#29ABE2", LIGHT = "#E6F1FB", MID = "#185FA5";

const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;700;900&family=DM+Sans:wght@300;400;500;600&display=swap');
*,*::before,*::after{box-sizing:border-box;margin:0;padding:0;}
body{font-family:'DM Sans',sans-serif;background:#F0F4F8;color:#1a1a2e;overflow-x:hidden;}
input,select,textarea,button{font-family:'DM Sans',sans-serif;}
input[type=file]{display:none;}
::-webkit-scrollbar{width:4px;}::-webkit-scrollbar-thumb{background:${SKY};border-radius:4px;}
@keyframes fadeUp{from{opacity:0;transform:translateY(18px);}to{opacity:1;transform:translateY(0);}}
@keyframes fadeIn{from{opacity:0;}to{opacity:1;}}
@keyframes pulse{0%,100%{box-shadow:0 0 0 0 rgba(41,171,226,.5);}70%{box-shadow:0 0 0 12px rgba(41,171,226,0);}}
@keyframes scanLine{0%{top:5%;}50%{top:88%;}100%{top:5%;}}
@keyframes float{0%,100%{transform:translateY(0);}50%{transform:translateY(-8px);}}
input:focus,select:focus,textarea:focus{outline:none;border-color:${SKY}!important;box-shadow:0 0 0 3px rgba(41,171,226,.1);}
`;

// ─── UI Primitives ────────────────────────────────────────
const Btn = ({ children, onClick, variant = "primary", disabled, full, size = "md", style = {} }) => {
  const sz = { sm: "7px 14px", md: "11px 20px", lg: "14px 28px" };
  const v = {
    primary: { background: SKY, color: NAVY, border: "none" },
    navy:    { background: NAVY, color: "#fff", border: "none" },
    outline: { background: "transparent", color: NAVY, border: `2px solid ${NAVY}` },
    ghost:   { background: "rgba(255,255,255,.08)", color: "#fff", border: "1px solid rgba(255,255,255,.2)" },
    success: { background: "#1D9E75", color: "#fff", border: "none" },
    danger:  { background: "#E24B4A", color: "#fff", border: "none" },
    light:   { background: LIGHT, color: MID, border: `1.5px solid ${SKY}` },
  };
  return (
    <button
      onClick={disabled ? undefined : onClick}
      style={{
        padding: sz[size], borderRadius: 9, fontSize: size === "sm" ? 12 : 14,
        fontWeight: 600, cursor: disabled ? "not-allowed" : "pointer",
        width: full ? "100%" : "auto", opacity: disabled ? 0.4 : 1,
        transition: "all .18s", display: "inline-flex", alignItems: "center",
        justifyContent: "center", gap: 6, ...(v[variant] || v.primary), ...style
      }}
    >{children}</button>
  );
};

const Field = ({ label, required, span, hint, children }) => (
  <div style={{ display: "flex", flexDirection: "column", gap: 5, gridColumn: span ? "1/-1" : undefined }}>
    {label && (
      <label style={{ fontSize: 11, fontWeight: 600, color: "#64748B", letterSpacing: ".03em" }}>
        {label}{required && <span style={{ color: "#E24B4A" }}> *</span>}
      </label>
    )}
    {children}
    {hint && <span style={{ fontSize: 11, color: "#94A3B8" }}>{hint}</span>}
  </div>
);

const Inp = ({ label, type = "text", value, onChange, placeholder, required, span, hint }) => (
  <Field label={label} required={required} span={span} hint={hint}>
    <input
      type={type} value={value} onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
      style={{ background: "#F8FAFC", border: "1.5px solid #E2E8F0", borderRadius: 9, padding: "10px 14px", fontSize: 14, color: "#1a1a2e", width: "100%" }}
    />
  </Field>
);

const Sel = ({ label, value, onChange, opts, required, span }) => (
  <Field label={label} required={required} span={span}>
    <select
      value={value} onChange={e => onChange(e.target.value)}
      style={{ background: "#F8FAFC", border: "1.5px solid #E2E8F0", borderRadius: 9, padding: "10px 14px", fontSize: 14, color: "#1a1a2e", cursor: "pointer", width: "100%" }}
    >
      {opts.map(o => <option key={o.v || o} value={o.v || o}>{o.l || o}</option>)}
    </select>
  </Field>
);

const TA = ({ label, value, onChange, placeholder, rows = 3, span }) => (
  <Field label={label} span={span}>
    <textarea
      value={value} onChange={e => onChange(e.target.value)} rows={rows} placeholder={placeholder}
      style={{ background: "#F8FAFC", border: "1.5px solid #E2E8F0", borderRadius: 9, padding: "10px 14px", fontSize: 14, color: "#1a1a2e", resize: "vertical", width: "100%" }}
    />
  </Field>
);

const Card = ({ children, style = {} }) => (
  <div style={{ background: "#fff", borderRadius: 18, border: "1px solid #E2E8F0", padding: "22px", ...style }}>
    {children}
  </div>
);

const Bdg = ({ children, v = "blue" }) => {
  const m = {
    blue:  { bg: LIGHT, c: MID },
    green: { bg: "#EAF3DE", c: "#27500A" },
    amber: { bg: "#FAEEDA", c: "#633806" },
    navy:  { bg: NAVY, c: SKY },
    sky:   { bg: SKY, c: NAVY },
    red:   { bg: "#FCEBEB", c: "#A32D2D" },
  };
  const s = m[v] || m.blue;
  return (
    <span style={{ background: s.bg, color: s.c, fontSize: 11, fontWeight: 600, padding: "2px 9px", borderRadius: 20, letterSpacing: ".04em", whiteSpace: "nowrap" }}>
      {children}
    </span>
  );
};

const PBar = ({ step, total, labels }) => (
  <div style={{ marginBottom: 24 }}>
    <div style={{ display: "flex", gap: 4, marginBottom: 7 }}>
      {Array.from({ length: total }).map((_, i) => (
        <div key={i} style={{ flex: 1, height: 5, borderRadius: 4, background: i < step ? SKY : i === step ? MID : "#E2E8F0", transition: "all .4s" }} />
      ))}
    </div>
    {labels && (
      <div style={{ display: "flex", justifyContent: "space-between" }}>
        {labels.map((l, i) => (
          <span key={l} style={{ fontSize: 10, color: i <= step ? MID : "#CBD5E1", fontWeight: i === step ? 700 : 400 }}>{l}</span>
        ))}
      </div>
    )}
  </div>
);

const SHdr = ({ step, total, icon, title, sub }) => (
  <div style={{ marginBottom: 20, paddingBottom: 16, borderBottom: "1px solid #F1F5F9" }}>
    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
      <div style={{ width: 36, height: 36, borderRadius: "50%", background: LIGHT, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, flexShrink: 0 }}>
        {icon}
      </div>
      <div>
        <div style={{ fontSize: 11, color: SKY, fontWeight: 600, letterSpacing: ".1em", textTransform: "uppercase" }}>
          Step {step + 1} of {total}
        </div>
        <h2 style={{ fontFamily: "'Playfair Display',serif", fontSize: 20, color: NAVY, lineHeight: 1.2 }}>{title}</h2>
      </div>
    </div>
    {sub && <p style={{ fontSize: 13, color: "#64748B", lineHeight: 1.6 }}>{sub}</p>}
  </div>
);

const Nav = ({ step, total, onBack, onNext, nextLbl, disabled }) => (
  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 18, paddingTop: 14, borderTop: "1px solid #F1F5F9" }}>
    <Btn variant="outline" onClick={onBack} disabled={step === 0}>← Back</Btn>
    <span style={{ fontSize: 11, color: "#CBD5E1", fontWeight: 500 }}>{step + 1} / {total}</span>
    <Btn variant="navy" onClick={onNext} disabled={disabled}>{nextLbl || "Continue →"}</Btn>
  </div>
);

const RS = ({ title, items }) => (
  <div style={{ marginBottom: 16 }}>
    <div style={{ fontSize: 11, fontWeight: 700, color: "#94A3B8", letterSpacing: ".1em", textTransform: "uppercase", marginBottom: 9, paddingBottom: 6, borderBottom: "1px solid #F1F5F9" }}>
      {title}
    </div>
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "5px 18px" }}>
      {items.map(([l, v]) => (
        <div key={l}>
          <div style={{ fontSize: 11, color: "#94A3B8" }}>{l}</div>
          <div style={{ fontSize: 13, color: NAVY, fontWeight: 500, marginTop: 2, wordBreak: "break-word" }}>{v || "—"}</div>
        </div>
      ))}
    </div>
  </div>
);

// ─── Upload Zone ──────────────────────────────────────────
const UZone = ({ label, accept, file, onFile, hint, required }) => {
  const ref = useRef();
  const [drag, setDrag] = useState(false);
  return (
    <Field label={label} required={required}>
      <div
        onClick={() => ref.current.click()}
        onDragOver={e => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={e => { e.preventDefault(); setDrag(false); const f = e.dataTransfer.files[0]; if (f) onFile(f); }}
        style={{ border: `2px dashed ${file ? "#1D9E75" : drag ? MID : "#CBD5E1"}`, borderRadius: 12, padding: "18px 14px", textAlign: "center", cursor: "pointer", background: file ? "#EAF3DE" : drag ? "#F0F9FF" : "#FAFBFC", transition: "all .2s" }}
      >
        <input ref={ref} type="file" accept={accept} onChange={e => onFile(e.target.files[0])} />
        <div style={{ fontSize: 24, marginBottom: 5 }}>{file ? "✅" : "📎"}</div>
        <div style={{ fontSize: 12, color: file ? "#27500A" : "#94A3B8", fontWeight: file ? 600 : 400 }}>
          {file ? file.name : "Click or drag & drop to upload"}
        </div>
        {hint && <div style={{ fontSize: 10, color: "#CBD5E1", marginTop: 3 }}>{hint}</div>}
      </div>
    </Field>
  );
};

// ─── Face Capture ─────────────────────────────────────────
const FaceCapture = ({ captured, onCapture }) => {
  const vRef = useRef(), cRef = useRef();
  const [streaming, setStreaming] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [done, setDone] = useState(!!captured);

  const start = async () => {
    try {
      const s = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "user" } });
      vRef.current.srcObject = s;
      vRef.current.play();
      setStreaming(true);
    } catch {
      alert("Camera access required. Please allow camera permissions in your browser.");
    }
  };

  const snap = () => {
    setScanning(true);
    setTimeout(() => {
      const c = cRef.current, ctx = c.getContext("2d");
      c.width = 280; c.height = 280;
      ctx.drawImage(vRef.current, 0, 0, 280, 280);
      const url = c.toDataURL("image/jpeg", 0.9);
      vRef.current.srcObject?.getTracks().forEach(t => t.stop());
      setStreaming(false); setScanning(false); setDone(true);
      onCapture(url);
    }, 2500);
  };

  if (done && captured) return (
    <div style={{ textAlign: "center", padding: "8px 0" }}>
      <div style={{ position: "relative", display: "inline-block", marginBottom: 14 }}>
        <img src={captured} alt="ID" style={{ width: 180, height: 180, borderRadius: "50%", objectFit: "cover", border: `5px solid ${SKY}`, display: "block" }} />
        <div style={{ position: "absolute", bottom: 6, right: 6, width: 34, height: 34, borderRadius: "50%", background: "#1D9E75", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16, border: "3px solid #fff" }}>✓</div>
      </div>
      <div style={{ marginBottom: 10 }}><Bdg v="green">✅ Identity photo captured</Bdg></div>
      <Btn variant="outline" size="sm" onClick={() => setDone(false)}>Retake Photo</Btn>
    </div>
  );

  return (
    <div style={{ textAlign: "center" }}>
      <canvas ref={cRef} style={{ display: "none" }} />
      <div style={{ position: "relative", width: 260, height: 260, margin: "0 auto 16px", borderRadius: "50%", overflow: "hidden", border: `4px solid ${streaming ? SKY : "#E2E8F0"}`, background: NAVY }}>
        <video ref={vRef} style={{ width: "100%", height: "100%", objectFit: "cover" }} muted playsInline />
        {!streaming && (
          <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 8 }}>
            <div style={{ fontSize: 48 }}>📷</div>
            <div style={{ fontSize: 12, color: "rgba(255,255,255,.6)" }}>Camera preview</div>
          </div>
        )}
        {scanning && (
          <>
            <div style={{ position: "absolute", inset: 0, background: "rgba(12,35,64,.35)" }} />
            <div style={{ position: "absolute", left: "8%", right: "8%", height: 3, background: SKY, top: "50%", animation: "scanLine 2.5s ease-in-out infinite", boxShadow: `0 0 16px ${SKY}` }} />
            <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
              <div style={{ fontSize: 12, color: "#fff", fontWeight: 700, background: "rgba(12,35,64,.75)", padding: "6px 16px", borderRadius: 20 }}>SCANNING…</div>
            </div>
          </>
        )}
      </div>
      <div style={{ display: "flex", gap: 10, justifyContent: "center", marginBottom: 12 }}>
        {!streaming
          ? <Btn variant="navy" onClick={start}>Enable Camera</Btn>
          : <Btn variant="primary" onClick={snap} disabled={scanning}>{scanning ? "Processing…" : "📸 Capture Photo"}</Btn>
        }
      </div>
      <p style={{ fontSize: 12, color: "#94A3B8", lineHeight: 1.6, maxWidth: 280, margin: "0 auto" }}>
        Face the camera directly in good lighting. Your photo will be matched to your uploaded ID.
      </p>
    </div>
  );
};

// ─── Interview Scheduler ──────────────────────────────────
const Scheduler = ({ selected, onChange }) => {
  const today = new Date();
  const slots = [];
  for (let d = 1; d <= 14; d++) {
    const dt = new Date(today);
    dt.setDate(today.getDate() + d);
    if (dt.getDay() !== 0 && dt.getDay() !== 6) {
      const lbl = dt.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" });
      const key = dt.toISOString().split("T")[0];
      ["09:00", "09:30", "10:00", "10:30", "11:00", "14:00", "14:30", "15:00", "15:30", "16:00"].forEach(t => slots.push({ date: lbl, key, time: t }));
    }
  }
  const days = [...new Set(slots.map(s => s.date))];
  const [day, setDay] = useState(days[0]);
  const daySlots = slots.filter(s => s.date === day);

  return (
    <div>
      <div style={{ display: "flex", gap: 6, overflowX: "auto", paddingBottom: 8, marginBottom: 14, scrollbarWidth: "none" }}>
        {days.slice(0, 10).map(d => (
          <button key={d} onClick={() => setDay(d)}
            style={{ padding: "7px 13px", borderRadius: 9, border: `1.5px solid ${day === d ? SKY : "#E2E8F0"}`, background: day === d ? LIGHT : "#fff", cursor: "pointer", fontSize: 12, fontWeight: day === d ? 700 : 400, color: day === d ? MID : "#64748B", whiteSpace: "nowrap", flexShrink: 0, transition: "all .18s" }}>
            {d}
          </button>
        ))}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(5,1fr)", gap: 7 }}>
        {daySlots.map(s => {
          const k = `${s.key}_${s.time}`, sel = selected === k;
          return (
            <button key={k} onClick={() => onChange(sel ? "" : k)}
              style={{ padding: "9px 4px", borderRadius: 8, border: `1.5px solid ${sel ? SKY : "#E2E8F0"}`, background: sel ? LIGHT : "#fff", cursor: "pointer", fontSize: 13, fontWeight: sel ? 700 : 400, color: sel ? MID : "#1a1a2e", transition: "all .18s" }}>
              {s.time}
            </button>
          );
        })}
      </div>
      {selected && (
        <div style={{ marginTop: 12, padding: "10px 14px", background: LIGHT, borderRadius: 9, display: "flex", alignItems: "center", gap: 9, border: `1.5px solid ${SKY}` }}>
          <span style={{ fontSize: 18 }}>📅</span>
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, color: NAVY }}>Interview Scheduled</div>
            <div style={{ fontSize: 12, color: MID }}>{selected.replace("_", " at ")}</div>
          </div>
          <Bdg v="green">Confirmed</Bdg>
        </div>
      )}
    </div>
  );
};

// ─── Work Fields ──────────────────────────────────────────
const FIELDS = [
  { id: "hsc", l: "Healthcare & Social Care", i: "🏥" },
  { id: "mhs", l: "Mental Health Support", i: "🧠" },
  { id: "sar", l: "Substance Abuse Recovery", i: "💙" },
  { id: "ldc", l: "Learning Disabilities Care", i: "🌟" },
  { id: "rsc", l: "Residential Care", i: "🏠" },
  { id: "dom", l: "Domiciliary Care", i: "🚗" },
  { id: "nrs", l: "Nursing", i: "💉" },
  { id: "adm", l: "Administration & Office", i: "💼" },
  { id: "fin", l: "Finance & Accounting", i: "💰" },
  { id: "ite", l: "IT & Technology", i: "💻" },
  { id: "eng", l: "Engineering", i: "⚙️" },
  { id: "cst", l: "Construction & Trades", i: "🔨" },
  { id: "log", l: "Logistics & Transport", i: "🚚" },
  { id: "drv", l: "Driving", i: "🚘" },
  { id: "ret", l: "Retail & Customer Service", i: "🛍️" },
  { id: "hos", l: "Hospitality & Catering", i: "🍽️" },
  { id: "edu", l: "Education & Training", i: "🎓" },
  { id: "cln", l: "Cleaning & Facilities", i: "✨" },
  { id: "sec", l: "Security", i: "🔒" },
  { id: "mkt", l: "Marketing & Comms", i: "📣" },
  { id: "leg", l: "Legal & Compliance", i: "⚖️" },
  { id: "prj", l: "Project Management", i: "📋" },
  { id: "hr",  l: "Human Resources", i: "👥" },
  { id: "sal", l: "Sales", i: "📈" },
];

// ─── Reference Form ───────────────────────────────────────
const RefForm = ({ index, val, onChange }) => {
  const f = k => v => onChange({ ...val, [k]: v });
  return (
    <Card style={{ marginBottom: 12, background: "#FAFBFC" }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: NAVY, marginBottom: 12, display: "flex", alignItems: "center", gap: 8 }}>
        <div style={{ width: 22, height: 22, borderRadius: "50%", background: NAVY, color: SKY, fontSize: 11, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center" }}>{index + 1}</div>
        {index === 0 ? "Most Recent Employer" : "Second Reference (Professional or Character)"}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 11 }}>
        <Inp label="Full name" value={val.name} onChange={f("name")} placeholder="Dr. Jane Smith" required />
        <Inp label="Job title" value={val.title} onChange={f("title")} placeholder="Line Manager" />
        <Inp label="Company" value={val.company} onChange={f("company")} placeholder="Organisation name" />
        <Inp label="Relationship" value={val.rel} onChange={f("rel")} placeholder="e.g. Line Manager" />
        <Inp label="Phone" type="tel" value={val.phone} onChange={f("phone")} placeholder="07xxx xxxxxx" />
        <Inp label="Email" type="email" value={val.email} onChange={f("email")} placeholder="ref@email.com" />
        <Sel label="Known how long?" value={val.dur} onChange={f("dur")} opts={["Select", "<1 year", "1–2 years", "2–5 years", "5+ years"]} />
        <Sel label="Permission to contact?" value={val.perm} onChange={f("perm")} opts={["Select", "Yes — anytime", "After offer only", "Written only"]} />
      </div>
    </Card>
  );
};

// ─────────────────────────────────────────────────────────
// JOB SEEKER APP
// ─────────────────────────────────────────────────────────
const SSTEPS = ["Personal", "Work Fields", "Experience", "Documents", "References", "Face ID", "Interview", "Review"];
const eRef = () => ({ name: "", title: "", company: "", rel: "", phone: "", email: "", dur: "Select", perm: "Select" });
const eS = () => ({
  fn: "", ln: "", dob: "", gender: "", phone: "", email: "", addr: "", city: "", pc: "", nat: "", ni: "", rtw: "", drv: "", contact: "",
  fields: [], curRole: "", exp: "", empType: "", salary: "", workLoc: "", avail: "", about: "",
  docId: null, docDbs: null, docCv: null, docQual: null, docProof: null,
  refs: [eRef(), eRef()], face: null, slot: "", consent: false,
});

function SeekerApp({ onBack }) {
  const [step, setStep] = useState(0);
  const [d, setD] = useState(eS());
  const [done, setDone] = useState(false);
  const set = k => v => setD(x => ({ ...x, [k]: v }));
  const tF = id => setD(x => ({ ...x, fields: x.fields.includes(id) ? x.fields.filter(i => i !== id) : [...x.fields, id] }));
  const uRef = (i, v) => { const r = [...d.refs]; r[i] = v; setD(x => ({ ...x, refs: r })); };

  if (done) return (
    <div style={{ maxWidth: 540, margin: "0 auto", padding: "48px 14px", textAlign: "center", animation: "fadeIn .5s ease" }}>
      <div style={{ width: 88, height: 88, borderRadius: "50%", background: SKY, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 40, margin: "0 auto 22px", animation: "pulse 2s infinite" }}>✓</div>
      <h1 style={{ fontFamily: "'Playfair Display',serif", fontSize: 26, color: NAVY, marginBottom: 10 }}>Registered, {d.fn}!</h1>
      <p style={{ fontSize: 14, color: "#64748B", lineHeight: 1.7, marginBottom: 26 }}>
        Your profile has been submitted. A Path Charis consultant will be in touch within 24 hours.
      </p>
      {[
        ["📋", "Registration submitted successfully"],
        ["🔍", "Documents sent for verification (24hrs)"],
        ["📞", "Consultant will call within 24 hours"],
        ["📅", "Your interview slot is confirmed"],
        ["🔔", "Follow @pathcharis_uk for live vacancies"],
      ].map(([ic, tx]) => (
        <div key={tx} style={{ display: "flex", gap: 12, alignItems: "center", padding: "11px 14px", background: "#F8FAFC", borderRadius: 10, fontSize: 13, color: NAVY, fontWeight: 500, marginBottom: 8, textAlign: "left" }}>
          <span style={{ fontSize: 18 }}>{ic}</span>{tx}
        </div>
      ))}
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", justifyContent: "center", margin: "18px 0" }}>
        <Bdg v="navy">📞 07486 883 393</Bdg>
        <Bdg v="blue">🌐 www.pathcharis.co.uk</Bdg>
        <Bdg v="sky">📱 @pathcharis_uk</Bdg>
      </div>
      <Btn variant="navy" onClick={onBack} full>Return to Home</Btn>
    </div>
  );

  return (
    <div style={{ maxWidth: 700, margin: "0 auto", padding: "0 14px 60px" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "18px 0 22px" }}>
        <button onClick={onBack} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 22, color: NAVY, lineHeight: 1 }}>←</button>
        <div>
          <h1 style={{ fontFamily: "'Playfair Display',serif", fontSize: 21, color: NAVY }}>Job Seeker Registration</h1>
          <p style={{ fontSize: 11, color: "#64748B" }}>Path Charis Recruitments · Always free for candidates</p>
        </div>
      </div>
      <PBar step={step} total={SSTEPS.length} labels={SSTEPS} />

      {step === 0 && (
        <Card>
          <SHdr step={0} total={8} icon="👤" title="Personal Information" sub="Your details are protected and never shared without your written consent." />
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 13 }}>
            <Inp label="First name" value={d.fn} onChange={set("fn")} placeholder="Amara" required />
            <Inp label="Last name" value={d.ln} onChange={set("ln")} placeholder="Johnson" required />
            <Inp label="Date of birth" type="date" value={d.dob} onChange={set("dob")} required />
            <Sel label="Gender" value={d.gender} onChange={set("gender")} opts={["Prefer not to say", "Male", "Female", "Non-binary", "Other"]} />
            <Inp label="Phone number" type="tel" value={d.phone} onChange={set("phone")} placeholder="07xxx xxxxxx" required />
            <Inp label="Email address" type="email" value={d.email} onChange={set("email")} placeholder="you@email.com" required />
            <Inp label="Street address" value={d.addr} onChange={set("addr")} placeholder="123 High Street" span required />
            <Inp label="City / Town" value={d.city} onChange={set("city")} placeholder="London" required />
            <Inp label="Postcode" value={d.pc} onChange={set("pc")} placeholder="SW1A 1AA" required />
            <Inp label="Nationality" value={d.nat} onChange={set("nat")} placeholder="British" required />
            <Inp label="National Insurance no." value={d.ni} onChange={set("ni")} placeholder="AB 12 34 56 C" />
            <Sel label="Right to work in UK" value={d.rtw} onChange={set("rtw")} required
              opts={["Select", "UK Citizen / Passport", "EU Settled Status", "EU Pre-Settled", "Skilled Worker Visa", "Student Visa", "Spousal Visa", "Other visa", "Seeking sponsorship"]} />
            <Sel label="Driving licence?" value={d.drv} onChange={set("drv")}
              opts={["Select", "Full UK + own vehicle", "Full UK licence", "Provisional", "No licence"]} />
            <Sel label="Preferred contact" value={d.contact} onChange={set("contact")}
              opts={["Select", "Phone call", "WhatsApp", "Email", "Any"]} />
          </div>
        </Card>
      )}

      {step === 1 && (
        <Card>
          <SHdr step={1} total={8} icon="🎯" title="Fields You Can Work In" sub="Select all sectors that match your experience and skills." />
          <div style={{ display: "flex", flexWrap: "wrap", gap: 7, marginBottom: 12 }}>
            {FIELDS.map(f => {
              const a = d.fields.includes(f.id);
              return (
                <div key={f.id} onClick={() => tF(f.id)}
                  style={{ padding: "7px 13px", borderRadius: 20, fontSize: 12, fontWeight: 500, cursor: "pointer", display: "flex", alignItems: "center", gap: 5, border: `1.5px solid ${a ? SKY : "#E2E8F0"}`, background: a ? LIGHT : "#fff", color: a ? MID : "#64748B", transition: "all .18s", userSelect: "none" }}>
                  <span style={{ fontSize: 13 }}>{f.i}</span>{f.l}
                  {a && <span style={{ fontSize: 11, fontWeight: 700, color: SKY }}>✓</span>}
                </div>
              );
            })}
          </div>
          {d.fields.length > 0 && (
            <div style={{ padding: "9px 13px", background: LIGHT, borderRadius: 9, display: "flex", alignItems: "center", gap: 9, border: `1.5px solid ${SKY}` }}>
              <span style={{ fontSize: 18 }}>🎯</span>
              <div style={{ fontSize: 13, color: MID, fontWeight: 600 }}>
                {d.fields.length} field{d.fields.length !== 1 ? "s" : ""} selected
              </div>
              <div style={{ fontSize: 12, color: "#64748B", flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {FIELDS.filter(f => d.fields.includes(f.id)).map(f => f.l).join(", ")}
              </div>
            </div>
          )}
        </Card>
      )}

      {step === 2 && (
        <Card>
          <SHdr step={2} total={8} icon="💼" title="Your Experience & Preferences" sub="Help us match you to the right opportunities." />
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 13 }}>
            <Inp label="Current / most recent role" value={d.curRole} onChange={set("curRole")} placeholder="Healthcare Support Worker" span />
            <Sel label="Years of experience" value={d.exp} onChange={set("exp")}
              opts={["Select", "<1 year", "1–2 years", "2–3 years", "3–5 years", "5–7 years", "7–10 years", "10+ years"]} />
            <Sel label="Employment type sought" value={d.empType} onChange={set("empType")}
              opts={["Select", "Full-time permanent", "Part-time", "Temporary / contract", "Seasonal", "Zero hours", "Open to all"]} />
            <Sel label="Salary expectation" value={d.salary} onChange={set("salary")}
              opts={["Select", "National Living Wage", "£18–22k", "£22–26k", "£26–30k", "£30–35k", "£35–40k", "£40–50k", "£50k+", "Hourly – flexible"]} />
            <Sel label="Preferred work location" value={d.workLoc} onChange={set("workLoc")}
              opts={["Select", "On-site", "Remote", "Hybrid", "No preference"]} />
            <Sel label="Availability" value={d.avail} onChange={set("avail")}
              opts={["Select", "Immediately", "Within 1 week", "Within 2 weeks", "Within 1 month", "Within 3 months", "Currently employed"]} />
            <TA label="About you — key skills, achievements, what makes you stand out" value={d.about} onChange={set("about")} rows={4}
              placeholder="Describe your experience, key skills and what you are looking for..." span />
          </div>
        </Card>
      )}

      {step === 3 && (
        <Card>
          <SHdr step={3} total={8} icon="📁" title="Upload Your Documents" sub="All files are encrypted. Reviewed only by Path Charis compliance team within 24 hours." />
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 14 }}>
            <UZone label="Passport or Photo ID" accept="image/*,.pdf" file={d.docId} onFile={v => set("docId")(v)} hint="JPG, PNG or PDF · Max 10MB" required />
            <UZone label="DBS Certificate" accept="image/*,.pdf" file={d.docDbs} onFile={v => set("docDbs")(v)} hint="Basic or Enhanced DBS" />
            <UZone label="CV / Résumé" accept=".pdf,.doc,.docx" file={d.docCv} onFile={v => set("docCv")(v)} hint="PDF or Word · Max 5MB" required />
            <UZone label="Qualifications / Certificates" accept="image/*,.pdf" file={d.docQual} onFile={v => set("docQual")(v)} hint="NVQ, degree, professional certs" />
            <UZone label="Proof of Right to Work" accept="image/*,.pdf" file={d.docProof} onFile={v => set("docProof")(v)} hint="Visa, BRP card, share code" />
          </div>
          <div style={{ padding: "11px 14px", background: "#FEF3E8", borderRadius: 9, fontSize: 12, color: "#854F0B", lineHeight: 1.6, border: "1px solid #FAC775" }}>
            ⚠️ Documents are reviewed privately. Never shared without your explicit written consent.
          </div>
        </Card>
      )}

      {step === 4 && (
        <Card>
          <SHdr step={4} total={8} icon="📝" title="Professional References" sub="We contact references only after a conditional offer — and only with your permission." />
          {d.refs.map((ref, i) => <RefForm key={i} index={i} val={ref} onChange={v => uRef(i, v)} />)}
          <div style={{ padding: "10px 13px", background: LIGHT, borderRadius: 9, fontSize: 12, color: MID, lineHeight: 1.6 }}>
            💡 If you cannot provide an employer reference, a character reference from a teacher, mentor or community leader is acceptable for reference 2.
          </div>
        </Card>
      )}

      {step === 5 && (
        <Card>
          <SHdr step={5} total={8} icon="🔍" title="Identity Verification — Face Capture" sub="Your photo is matched to your uploaded ID to verify your identity. Required for all candidates." />
          <FaceCapture captured={d.face} onCapture={v => set("face")(v)} />
        </Card>
      )}

      {step === 6 && (
        <Card>
          <SHdr step={6} total={8} icon="📅" title="Schedule Your Registration Interview" sub="Book a 20-minute call with your dedicated Path Charis consultant." />
          <Scheduler selected={d.slot} onChange={v => set("slot")(v)} />
          <div style={{ marginTop: 14, padding: "10px 13px", background: LIGHT, borderRadius: 9, fontSize: 12, color: MID, lineHeight: 1.6 }}>
            📋 Interviews by phone, Microsoft Teams or Zoom. Confirmation sent to your email.
          </div>
        </Card>
      )}

      {step === 7 && (
        <Card>
          <SHdr step={7} total={8} icon="✅" title="Review & Submit" sub="Check everything carefully before submitting your registration." />
          <RS title="Personal Details" items={[
            ["Name", `${d.fn} ${d.ln}`], ["Phone", d.phone], ["Email", d.email],
            ["Location", `${d.city}, ${d.pc}`], ["Right to work", d.rtw], ["Nationality", d.nat],
          ]} />
          <RS title="Work Fields" items={[
            ["Selected", FIELDS.filter(f => d.fields.includes(f.id)).map(f => f.l).join(", ") || "None selected"],
          ]} />
          <RS title="Experience & Preferences" items={[
            ["Current role", d.curRole], ["Experience", d.exp],
            ["Employment type", d.empType], ["Salary", d.salary],
            ["Work location", d.workLoc], ["Availability", d.avail],
          ]} />
          <RS title="Documents" items={[
            ["Passport/ID", d.docId ? "✅ " + d.docId.name : "❌ Not uploaded"],
            ["DBS", d.docDbs ? "✅ " + d.docDbs.name : "Not provided"],
            ["CV", d.docCv ? "✅ " + d.docCv.name : "❌ Not uploaded"],
            ["Qualifications", d.docQual ? "✅ " + d.docQual.name : "Not provided"],
          ]} />
          <RS title="Verification & Interview" items={[
            ["Face capture", d.face ? "✅ Completed" : "❌ Not completed"],
            ["Interview slot", d.slot ? d.slot.replace("_", " at ") : "❌ Not selected"],
          ]} />
          <div style={{ marginTop: 16, padding: "14px", background: "#F8FAFC", borderRadius: 12, border: "1px solid #E2E8F0" }}>
            <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
              <input type="checkbox" id="consent" checked={d.consent} onChange={e => set("consent")(e.target.checked)}
                style={{ width: 18, height: 18, marginTop: 2, accentColor: NAVY, cursor: "pointer", flexShrink: 0 }} />
              <label htmlFor="consent" style={{ fontSize: 12, color: "#64748B", lineHeight: 1.6, cursor: "pointer" }}>
                I confirm all information is accurate. I consent to Path Charis Group storing and processing my personal data for recruitment purposes. My profile will never be shared with employers without my prior written consent.
              </label>
            </div>
          </div>
          {!d.consent && (
            <div style={{ marginTop: 8, fontSize: 12, color: "#E24B4A" }}>⚠️ Please accept the consent declaration before submitting.</div>
          )}
        </Card>
      )}

      <Nav
        step={step} total={SSTEPS.length}
        onBack={() => setStep(s => Math.max(s - 1, 0))}
        onNext={() => step < SSTEPS.length - 1 ? setStep(s => s + 1) : d.consent && setDone(true)}
        nextLbl={step === SSTEPS.length - 1 ? "Submit Registration ✓" : "Continue →"}
        disabled={step === SSTEPS.length - 1 && !d.consent}
      />
    </div>
  );
}

// ─────────────────────────────────────────────────────────
// TALENT POOL
// ─────────────────────────────────────────────────────────
const POOL = [
  { id: 1, name: "Amara Johnson", title: "Healthcare Support Worker", exp: "4 yrs", fields: ["Healthcare & Social Care", "Mental Health Support"], loc: "London", dbs: "Enhanced", avail: "Immediately", score: 98 },
  { id: 2, name: "James Okafor", title: "Residential Care Assistant", exp: "2 yrs", fields: ["Residential Care", "Learning Disabilities Care"], loc: "Birmingham", dbs: "Enhanced", avail: "2 weeks", score: 95 },
  { id: 3, name: "Priya Sharma", title: "HR & Admin Manager", exp: "7 yrs", fields: ["Human Resources", "Administration & Office"], loc: "Manchester", dbs: "Basic", avail: "1 month", score: 97 },
  { id: 4, name: "Marcus Clarke", title: "HGV Driver & Logistics Lead", exp: "9 yrs", fields: ["Logistics & Transport", "Driving"], loc: "Leeds", dbs: "Basic", avail: "Immediately", score: 94 },
  { id: 5, name: "Sophie Williams", title: "Mental Health Support Worker", exp: "3 yrs", fields: ["Mental Health Support", "Substance Abuse Recovery"], loc: "Bristol", dbs: "Enhanced", avail: "Immediately", score: 99 },
  { id: 6, name: "Daniel Mensah", title: "Senior IT Support Engineer", exp: "6 yrs", fields: ["IT & Technology"], loc: "London", dbs: "Basic", avail: "2 weeks", score: 96 },
  { id: 7, name: "Fatima Al-Hassan", title: "Early Years Educator", exp: "8 yrs", fields: ["Education & Training"], loc: "Sheffield", dbs: "Enhanced", avail: "1 month", score: 98 },
  { id: 8, name: "Tom Brennan", title: "Facilities & Cleaning Manager", exp: "6 yrs", fields: ["Cleaning & Facilities", "Security"], loc: "Liverpool", dbs: "Basic", avail: "Immediately", score: 93 },
  { id: 9, name: "Grace Nwosu", title: "Domiciliary & Community Carer", exp: "4 yrs", fields: ["Domiciliary Care", "Nursing"], loc: "Nottingham", dbs: "Enhanced", avail: "Immediately", score: 97 },
  { id: 10, name: "Ali Hassan", title: "Security & Facilities Officer", exp: "7 yrs", fields: ["Security", "Cleaning & Facilities"], loc: "London", dbs: "Basic", avail: "2 weeks", score: 92 },
  { id: 11, name: "Claire Murphy", title: "Finance & Payroll Officer", exp: "5 yrs", fields: ["Finance & Accounting", "Administration & Office"], loc: "Edinburgh", dbs: "Basic", avail: "1 month", score: 95 },
  { id: 12, name: "Kwame Asante", title: "Construction Site Supervisor", exp: "10 yrs", fields: ["Construction & Trades", "Project Management"], loc: "London", dbs: "Basic", avail: "2 weeks", score: 96 },
];

function TalentPool({ onBack }) {
  const [search, setSearch] = useState("");
  const [avail, setAvail] = useState("All");
  const [field, setField] = useState("All");
  const [sel, setSel] = useState(null);
  const [shortlisted, setShortlisted] = useState([]);

  const allFields = [...new Set(POOL.flatMap(c => c.fields))].sort();
  const filtered = POOL.filter(c => {
    const ms = !search || [c.name, c.title, c.loc].some(x => x.toLowerCase().includes(search.toLowerCase()));
    const ma = avail === "All" || c.avail === avail;
    const mf = field === "All" || c.fields.includes(field);
    return ms && ma && mf;
  });

  return (
    <div style={{ maxWidth: 860, margin: "0 auto", padding: "0 14px 60px" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "18px 0 16px" }}>
        <button onClick={onBack} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 22, color: NAVY, lineHeight: 1 }}>←</button>
        <div style={{ flex: 1 }}>
          <h1 style={{ fontFamily: "'Playfair Display',serif", fontSize: 21, color: NAVY }}>Verified Talent Pool</h1>
          <p style={{ fontSize: 11, color: "#64748B" }}>All candidates background-checked and verified by Path Charis</p>
        </div>
        <div style={{ display: "flex", gap: 7 }}>
          <Bdg v="green">{filtered.length} available</Bdg>
          {shortlisted.length > 0 && <Bdg v="navy">⭐ {shortlisted.length} shortlisted</Bdg>}
        </div>
      </div>

      <div style={{ display: "flex", gap: 9, marginBottom: 14, flexWrap: "wrap" }}>
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="🔍 Search name, role, location..."
          style={{ flex: 1, minWidth: 180, background: "#fff", border: "1.5px solid #E2E8F0", borderRadius: 9, padding: "9px 13px", fontSize: 14, color: "#1a1a2e" }} />
        <select value={avail} onChange={e => setAvail(e.target.value)}
          style={{ background: "#fff", border: "1.5px solid #E2E8F0", borderRadius: 9, padding: "9px 13px", fontSize: 13, cursor: "pointer" }}>
          {["All", "Immediately", "2 weeks", "1 month"].map(o => <option key={o}>{o}</option>)}
        </select>
        <select value={field} onChange={e => setField(e.target.value)}
          style={{ background: "#fff", border: "1.5px solid #E2E8F0", borderRadius: 9, padding: "9px 13px", fontSize: 13, cursor: "pointer", maxWidth: 200 }}>
          {["All", ...allFields].map(o => <option key={o}>{o}</option>)}
        </select>
      </div>

      <div style={{ padding: "12px 15px", background: LIGHT, borderRadius: 11, marginBottom: 14, display: "flex", gap: 10, alignItems: "flex-start", border: `1.5px solid ${SKY}` }}>
        <span style={{ fontSize: 20, flexShrink: 0 }}>🔒</span>
        <div style={{ fontSize: 12, color: MID, lineHeight: 1.5 }}>
          <strong>Path Charis Guarantee:</strong> Every candidate has passed identity verification, right-to-work checks, document verification and reference screening.
        </div>
      </div>

      <div style={{ display: "grid", gap: 10 }}>
        {filtered.map(c => (
          <div key={c.id} onClick={() => setSel(sel === c.id ? null : c.id)}
            style={{ background: "#fff", borderRadius: 16, border: `${sel === c.id ? 2 : 1}px solid ${sel === c.id ? SKY : "#E2E8F0"}`, padding: "16px 18px", cursor: "pointer", transition: "all .2s" }}>
            <div style={{ display: "flex", alignItems: "flex-start", gap: 13 }}>
              <div style={{ width: 48, height: 48, borderRadius: "50%", background: NAVY, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'Playfair Display',serif", fontSize: 16, fontWeight: 900, color: SKY }}>
                {c.name.split(" ").map(n => n[0]).join("")}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 5, flexWrap: "wrap" }}>
                  <span style={{ fontSize: 15, fontWeight: 700, color: NAVY }}>{c.name}</span>
                  <Bdg v="green">✓ Verified</Bdg>
                  <span style={{ fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 20, background: "#FAEEDA", color: "#633806" }}>Match {c.score}%</span>
                  <span style={{ marginLeft: "auto", fontSize: 11, fontWeight: 600, padding: "2px 9px", borderRadius: 20, background: c.avail === "Immediately" ? "#EAF3DE" : "#FAEEDA", color: c.avail === "Immediately" ? "#27500A" : "#854F0B" }}>
                    {c.avail === "Immediately" ? "⚡ Available now" : `Available in ${c.avail}`}
                  </span>
                </div>
                <div style={{ fontSize: 12, color: "#64748B", marginBottom: 7 }}>{c.title} · {c.exp} · {c.loc}</div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                  {c.fields.map(f => <span key={f} style={{ fontSize: 10, background: LIGHT, color: MID, padding: "2px 7px", borderRadius: 20 }}>{f}</span>)}
                  <span style={{ fontSize: 10, background: "#FEF3E8", color: "#854F0B", padding: "2px 7px", borderRadius: 20 }}>DBS: {c.dbs}</span>
                </div>
              </div>
            </div>
            {sel === c.id && (
              <div style={{ marginTop: 14, paddingTop: 14, borderTop: "1px solid #F1F5F9", animation: "fadeUp .3s ease" }}>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 9, marginBottom: 12 }}>
                  {[["Experience", c.exp], ["Location", c.loc], ["DBS Check", c.dbs]].map(([l, v]) => (
                    <div key={l} style={{ background: "#F8FAFC", borderRadius: 9, padding: "9px 11px" }}>
                      <div style={{ fontSize: 11, color: "#94A3B8", marginBottom: 2 }}>{l}</div>
                      <div style={{ fontSize: 13, fontWeight: 700, color: NAVY }}>{v}</div>
                    </div>
                  ))}
                </div>
                <div style={{ padding: "10px 13px", background: "#EAF3DE", borderRadius: 9, fontSize: 12, color: "#27500A", marginBottom: 12, border: "1px solid #C0DD97" }}>
                  ✅ Identity verified · Right to work confirmed · Documents authenticated · References screened
                </div>
                <div style={{ display: "flex", gap: 9, flexWrap: "wrap" }}>
                  <Btn variant="primary" size="sm" onClick={e => { e.stopPropagation(); alert(`✅ Interview request for ${c.name} sent to your Path Charis consultant.\n\nYou will receive available time slots within 2 business hours.`); }}>
                    📅 Request Interview
                  </Btn>
                  <Btn variant={shortlisted.includes(c.id) ? "success" : "outline"} size="sm"
                    onClick={e => { e.stopPropagation(); setShortlisted(s => s.includes(c.id) ? s.filter(x => x !== c.id) : [...s, c.id]); }}>
                    {shortlisted.includes(c.id) ? "⭐ Shortlisted" : "☆ Shortlist"}
                  </Btn>
                  <Btn variant="navy" size="sm" onClick={e => { e.stopPropagation(); alert(`📋 Full profile for ${c.name} will be emailed to you within 30 minutes.`); }}>
                    📋 Full Profile
                  </Btn>
                </div>
              </div>
            )}
          </div>
        ))}
        {filtered.length === 0 && (
          <div style={{ textAlign: "center", padding: "56px 20px", color: "#94A3B8" }}>
            <div style={{ fontSize: 44, marginBottom: 14 }}>🔍</div>
            <div style={{ fontSize: 16, fontWeight: 600, color: "#64748B", marginBottom: 7 }}>No candidates match your filters</div>
            <div style={{ fontSize: 13 }}>Try adjusting your search or availability filter</div>
          </div>
        )}
      </div>

      {shortlisted.length > 0 && (
        <div style={{ position: "sticky", bottom: 16, marginTop: 14 }}>
          <div style={{ background: NAVY, borderRadius: 14, padding: "13px 18px", display: "flex", alignItems: "center", gap: 12, boxShadow: "0 8px 28px rgba(12,35,64,.3)" }}>
            <span style={{ fontSize: 20 }}>⭐</span>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: "#fff" }}>{shortlisted.length} candidate{shortlisted.length !== 1 ? "s" : ""} shortlisted</div>
              <div style={{ fontSize: 12, color: "rgba(255,255,255,.55)" }}>Ready to send to your consultant</div>
            </div>
            <Btn variant="primary" size="sm" onClick={() => alert(`✅ Your shortlist of ${shortlisted.length} candidate${shortlisted.length !== 1 ? "s" : ""} has been sent to your Path Charis consultant.`)}>
              Send Shortlist →
            </Btn>
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────
// EMPLOYER APP
// ─────────────────────────────────────────────────────────
const ESTEPS = ["Company", "Contact & Role", "Requirements", "Terms"];
const eE = () => ({
  co: "", reg: "", ind: "", size: "", web: "", addr: "", city: "", pc: "",
  cn: "", ct: "", cp: "", ce: "", rt: "", rtype: "", rsal: "", rloc: "", urg: "", desc: "",
  rfields: [], dbs: "", minExp: "", trial: "", terms: false, gdpr: false, code: false,
});

function EmployerApp({ onBack }) {
  const [step, setStep] = useState(0);
  const [d, setD] = useState(eE());
  const [done, setDone] = useState(false);
  const [pool, setPool] = useState(false);
  const set = k => v => setD(x => ({ ...x, [k]: v }));
  const tF = id => setD(x => ({ ...x, rfields: x.rfields.includes(id) ? x.rfields.filter(i => i !== id) : [...x.rfields, id] }));

  if (pool) return <TalentPool onBack={() => setPool(false)} />;

  if (done) return (
    <div style={{ maxWidth: 560, margin: "0 auto", padding: "48px 14px", textAlign: "center", animation: "fadeIn .5s ease" }}>
      <div style={{ width: 88, height: 88, borderRadius: "50%", background: SKY, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 40, margin: "0 auto 22px", animation: "pulse 2s infinite" }}>🔓</div>
      <h1 style={{ fontFamily: "'Playfair Display',serif", fontSize: 26, color: NAVY, marginBottom: 10 }}>{d.co} — Access Granted</h1>
      <p style={{ fontSize: 14, color: "#64748B", lineHeight: 1.7, marginBottom: 24 }}>
        Your employer account is active. You have full access to Path Charis's verified talent pool.
      </p>
      {[
        ["✅", "Business verified and account activated"],
        ["🔓", "Full talent pool access enabled"],
        ["👤", "Dedicated Path Charis consultant assigned"],
        ["📋", "Shortlisting and interview tools active"],
        ["📞", "Path Charis: 07486 883 393"],
      ].map(([ic, tx]) => (
        <div key={tx} style={{ display: "flex", gap: 12, alignItems: "center", padding: "11px 14px", background: "#F8FAFC", borderRadius: 10, fontSize: 13, color: NAVY, fontWeight: 500, marginBottom: 8, textAlign: "left" }}>
          <span style={{ fontSize: 18 }}>{ic}</span>{tx}
        </div>
      ))}
      <div style={{ display: "flex", flexDirection: "column", gap: 11, marginTop: 20 }}>
        <Btn variant="primary" onClick={() => setPool(true)} full size="lg">🔍 Browse Verified Talent Pool Now</Btn>
        <Btn variant="outline" onClick={onBack} full>Return to Home</Btn>
      </div>
    </div>
  );

  return (
    <div style={{ maxWidth: 700, margin: "0 auto", padding: "0 14px 60px" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "18px 0 22px" }}>
        <button onClick={onBack} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 22, color: NAVY, lineHeight: 1 }}>←</button>
        <div>
          <h1 style={{ fontFamily: "'Playfair Display',serif", fontSize: 21, color: NAVY }}>Employer Registration</h1>
          <p style={{ fontSize: 11, color: "#64748B" }}>Path Charis Recruitments · Access verified talent instantly</p>
        </div>
      </div>
      <PBar step={step} total={ESTEPS.length} labels={ESTEPS} />

      {step === 0 && (
        <Card>
          <SHdr step={0} total={4} icon="🏢" title="Company Information" sub="We verify your business registration before granting talent pool access." />
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 13 }}>
            <Inp label="Company name" value={d.co} onChange={set("co")} placeholder="Acme Healthcare Ltd" required span />
            <Inp label="Companies House reg. number" value={d.reg} onChange={set("reg")} placeholder="12345678" required />
            <Sel label="Industry / sector" value={d.ind} onChange={set("ind")}
              opts={["Select", "Healthcare & Social Care", "Residential Care", "Education", "Logistics", "Retail", "Construction", "IT & Technology", "Finance", "Cleaning & Facilities", "Security", "Engineering", "Other"]} />
            <Sel label="Company size" value={d.size} onChange={set("size")}
              opts={["Select", "1–10 employees", "11–50 employees", "51–200 employees", "201–500 employees", "500+ employees"]} />
            <Inp label="Website" value={d.web} onChange={set("web")} placeholder="www.yourcompany.co.uk" span />
            <Inp label="Registered address" value={d.addr} onChange={set("addr")} placeholder="123 Business Park" required span />
            <Inp label="City / Town" value={d.city} onChange={set("city")} placeholder="London" required />
            <Inp label="Postcode" value={d.pc} onChange={set("pc")} placeholder="EC1A 1BB" required />
          </div>
        </Card>
      )}

      {step === 1 && (
        <Card>
          <SHdr step={1} total={4} icon="📋" title="Contact Person & Role to Fill" />
          <div style={{ marginBottom: 18, paddingBottom: 18, borderBottom: "1px solid #F1F5F9" }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: NAVY, marginBottom: 11 }}>Contact details</div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 13 }}>
              <Inp label="Contact name" value={d.cn} onChange={set("cn")} placeholder="Sarah Ahmed" required />
              <Inp label="Job title" value={d.ct} onChange={set("ct")} placeholder="HR Manager" required />
              <Inp label="Phone" type="tel" value={d.cp} onChange={set("cp")} placeholder="07xxx xxxxxx" required />
              <Inp label="Email" type="email" value={d.ce} onChange={set("ce")} placeholder="sarah@company.com" required />
            </div>
          </div>
          <div style={{ fontSize: 13, fontWeight: 700, color: NAVY, marginBottom: 11 }}>Role to fill</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 13 }}>
            <Inp label="Job title" value={d.rt} onChange={set("rt")} placeholder="Healthcare Support Worker" required span />
            <Sel label="Employment type" value={d.rtype} onChange={set("rtype")}
              opts={["Select", "Full-time permanent", "Part-time", "Temporary", "Seasonal", "Zero hours"]} />
            <Sel label="Salary" value={d.rsal} onChange={set("rsal")}
              opts={["Select", "National Living Wage", "£18–22k", "£22–26k", "£26–30k", "£30–35k", "£35–40k", "£40–50k", "£50k+"]} />
            <Sel label="Work location" value={d.rloc} onChange={set("rloc")}
              opts={["Select", "On-site", "Remote", "Hybrid", "Flexible"]} />
            <Sel label="Urgency" value={d.urg} onChange={set("urg")}
              opts={["Select", "Immediately", "Within 1 week", "Within 2 weeks", "Within 1 month", "Planning ahead"]} />
            <TA label="Role description & requirements" value={d.desc} onChange={set("desc")} rows={4}
              placeholder="Key responsibilities, essential skills and requirements..." span />
          </div>
        </Card>
      )}

      {step === 2 && (
        <Card>
          <SHdr step={2} total={4} icon="🎯" title="Candidate Requirements" sub="Filter the talent pool to match your exact needs." />
          <div style={{ marginBottom: 18 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: NAVY, marginBottom: 10 }}>Required sectors / fields</div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 7 }}>
              {FIELDS.map(f => {
                const a = d.rfields.includes(f.id);
                return (
                  <div key={f.id} onClick={() => tF(f.id)}
                    style={{ padding: "6px 12px", borderRadius: 20, fontSize: 12, fontWeight: 500, cursor: "pointer", display: "flex", alignItems: "center", gap: 5, border: `1.5px solid ${a ? SKY : "#E2E8F0"}`, background: a ? LIGHT : "#fff", color: a ? MID : "#64748B", transition: "all .18s", userSelect: "none" }}>
                    <span style={{ fontSize: 12 }}>{f.i}</span>{f.l}
                    {a && <span style={{ fontSize: 10, fontWeight: 700, color: SKY }}>✓</span>}
                  </div>
                );
              })}
            </div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 13 }}>
            <Sel label="DBS check required?" value={d.dbs} onChange={set("dbs")}
              opts={["Select", "Enhanced DBS", "Basic DBS", "Not required", "Path Charis to arrange"]} />
            <Sel label="Min. experience" value={d.minExp} onChange={set("minExp")}
              opts={["Select", "No minimum", "6 months+", "1 year+", "2 years+", "3 years+", "5 years+"]} />
            <Sel label="Probation period" value={d.trial} onChange={set("trial")}
              opts={["Select", "None", "1 month", "3 months", "6 months", "To be agreed"]} />
          </div>
          <div style={{ marginTop: 14, padding: "12px 14px", background: LIGHT, borderRadius: 9, fontSize: 12, color: MID, lineHeight: 1.5, border: `1px solid ${SKY}` }}>
            <strong>Path Charis Guarantee:</strong> Every candidate is pre-screened — right to work, ID, employment history, DBS and references all verified before you see their profile.
          </div>
        </Card>
      )}

      {step === 3 && (
        <Card>
          <SHdr step={3} total={4} icon="⚖️" title="Terms & Activate Access" sub="Review and confirm before accessing the verified talent pool." />
          {[
            { k: "terms", title: "Path Charis Employer Agreement", icon: "📋", body: "By registering you confirm your organisation is a legitimately UK-registered business. All roles must comply with UK employment law including the Equality Act 2010. You agree not to discriminate against any candidate on any protected characteristic.", lbl: "I accept the Employer Agreement" },
            { k: "gdpr", title: "Data Protection & UK GDPR", icon: "🔒", body: "Candidate data is provided solely to assess suitability for the registered role. You may not retain, share or use candidate data for any other purpose. All data must comply with UK GDPR.", lbl: "I confirm GDPR compliance" },
            { k: "code", title: "Code of Conduct", icon: "🤝", body: "You agree to treat all candidates with respect, provide timely interview feedback, and not contact candidates outside the Path Charis platform without their consent.", lbl: "I agree to the Code of Conduct" },
          ].map(s => (
            <div key={s.k} style={{ marginBottom: 14, padding: "14px", background: "#F8FAFC", borderRadius: 12, border: "1px solid #E2E8F0" }}>
              <div style={{ display: "flex", gap: 9, alignItems: "center", marginBottom: 8 }}>
                <span style={{ fontSize: 17 }}>{s.icon}</span>
                <div style={{ fontSize: 14, fontWeight: 700, color: NAVY }}>{s.title}</div>
              </div>
              <p style={{ fontSize: 12, color: "#64748B", lineHeight: 1.7, marginBottom: 11 }}>{s.body}</p>
              <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
                <input type="checkbox" id={s.k} checked={d[s.k]} onChange={e => set(s.k)(e.target.checked)}
                  style={{ width: 17, height: 17, accentColor: NAVY, cursor: "pointer", flexShrink: 0 }} />
                <label htmlFor={s.k} style={{ fontSize: 13, fontWeight: 600, color: NAVY, cursor: "pointer" }}>{s.lbl}</label>
              </div>
            </div>
          ))}
          <div style={{ padding: "14px", background: LIGHT, borderRadius: 12, border: `2px solid ${SKY}`, display: "flex", gap: 12, alignItems: "flex-start" }}>
            <div style={{ width: 42, height: 42, borderRadius: "50%", background: SKY, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20, flexShrink: 0 }}>🔓</div>
            <div>
              <div style={{ fontSize: 14, fontWeight: 700, color: NAVY, marginBottom: 4 }}>Instant access upon submission</div>
              <div style={{ fontSize: 12, color: "#64748B", lineHeight: 1.5 }}>
                Your account is activated immediately. Full access to our complete pool of {POOL.length} verified candidates — with shortlisting, interview requests and full profile tools.
              </div>
            </div>
          </div>
          {(!d.terms || !d.gdpr || !d.code) && (
            <div style={{ marginTop: 11, padding: "10px 13px", background: "#FEF3E8", borderRadius: 8, fontSize: 12, color: "#854F0B" }}>
              ⚠️ Please accept all three agreements to activate your account.
            </div>
          )}
        </Card>
      )}

      <Nav
        step={step} total={ESTEPS.length}
        onBack={() => setStep(s => Math.max(s - 1, 0))}
        onNext={() => step < ESTEPS.length - 1 ? setStep(s => s + 1) : (d.terms && d.gdpr && d.code) && setDone(true)}
        nextLbl={step === ESTEPS.length - 1 ? "🔓 Activate Access" : "Continue →"}
        disabled={step === ESTEPS.length - 1 && (!d.terms || !d.gdpr || !d.code)}
      />
    </div>
  );
}

// ─────────────────────────────────────────────────────────
// LANDING PAGE
// ─────────────────────────────────────────────────────────
function Landing({ onSeeker, onEmployer }) {
  return (
    <div style={{ minHeight: "100vh", background: `linear-gradient(145deg, ${NAVY} 0%, #0A1A30 55%, #071525 100%)` }}>
      <nav style={{ padding: "16px 28px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
          <div style={{ width: 36, height: 36, borderRadius: "50%", background: SKY, display: "flex", alignItems: "center", justifyContent: "center", position: "relative", flexShrink: 0 }}>
            <div style={{ position: "absolute", width: 9, height: 9, borderRadius: "50%", background: NAVY, top: 6, left: 4.5 }} />
            <div style={{ position: "absolute", width: 9, height: 9, borderRadius: "50%", background: NAVY, top: 6, right: 4.5 }} />
            <div style={{ position: "absolute", width: 8, height: 8, borderRadius: "50%", background: "#fff", top: 4 }} />
          </div>
          <div>
            <div style={{ fontFamily: "'Playfair Display',serif", fontSize: 16, fontWeight: 900, color: "#fff", lineHeight: 1.1 }}>Path Charis</div>
            <div style={{ fontSize: 8, color: SKY, letterSpacing: ".14em", textTransform: "uppercase" }}>Recruitments</div>
          </div>
        </div>
        <div style={{ display: "flex", gap: 9, alignItems: "center" }}>
          <span style={{ fontSize: 12, color: "rgba(255,255,255,.45)" }}>07486 883 393</span>
          <Btn variant="ghost" size="sm">pathcharis.co.uk</Btn>
        </div>
      </nav>

      <div style={{ textAlign: "center", padding: "44px 20px 0", maxWidth: 760, margin: "0 auto" }}>
        <div style={{ display: "inline-flex", alignItems: "center", gap: 8, background: "rgba(41,171,226,.12)", border: "1px solid rgba(41,171,226,.3)", color: SKY, fontSize: 11, fontWeight: 600, padding: "5px 16px", borderRadius: 20, letterSpacing: ".08em", textTransform: "uppercase", marginBottom: 26, animation: "fadeUp .8s ease" }}>
          <div style={{ width: 6, height: 6, borderRadius: "50%", background: SKY, animation: "pulse 2s infinite" }} />
          UK Recruitment Platform · Background-Checked Talent
        </div>

        <h1 style={{ fontFamily: "'Playfair Display',serif", fontSize: "clamp(32px,5vw,62px)", fontWeight: 900, color: "#fff", lineHeight: 1.06, marginBottom: 18, animation: "fadeUp .8s ease .1s both" }}>
          The right person.<br /><span style={{ color: SKY }}>Verified.</span> Ready.
        </h1>

        <p style={{ fontSize: "clamp(14px,1.8vw,16px)", color: "rgba(255,255,255,.65)", lineHeight: 1.75, marginBottom: 40, maxWidth: 500, margin: "0 auto 40px", animation: "fadeUp .8s ease .2s both" }}>
          Job seekers build a fully verified profile with face ID, documents and references.
          Employers access background-checked candidates — launched immediately.
        </p>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, maxWidth: 640, margin: "0 auto 40px", animation: "fadeUp .8s ease .3s both" }}>
          {[
            { icon: "👤", title: "I am looking\nfor work", badge: "Always Free", features: ["Complete verified profile", "Document upload & encryption", "Live face ID verification", "Interview scheduling", "24-hour consultant response"], label: "Register as Job Seeker →", fn: onSeeker, primary: true },
            { icon: "🏢", title: "I am hiring\nstaff", badge: "Instant Access", features: ["Pool of verified candidates", "All background checks done", "Shortlist & request interviews", "Full profile & document access", "Dedicated consultant assigned"], label: "Register as Employer →", fn: onEmployer, primary: false },
          ].map(({ icon, title, badge, features, label, fn, primary }) => (
            <div key={title} onClick={fn}
              style={{ background: primary ? "rgba(41,171,226,.08)" : "rgba(255,255,255,.04)", border: `1.5px solid ${primary ? "rgba(41,171,226,.35)" : "rgba(255,255,255,.14)"}`, borderRadius: 20, padding: "24px 18px", cursor: "pointer", textAlign: "left", transition: "all .22s" }}
              onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-4px)"; e.currentTarget.style.background = primary ? "rgba(41,171,226,.14)" : "rgba(255,255,255,.08)"; }}
              onMouseLeave={e => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.background = primary ? "rgba(41,171,226,.08)" : "rgba(255,255,255,.04)"; }}>
              <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 14 }}>
                <div style={{ fontSize: 36, animation: "float 4s ease-in-out infinite" }}>{icon}</div>
                <div style={{ fontSize: 10, fontWeight: 700, padding: "3px 10px", borderRadius: 20, background: primary ? SKY : "rgba(255,255,255,.15)", color: primary ? NAVY : "#fff", letterSpacing: ".05em" }}>{badge}</div>
              </div>
              <h2 style={{ fontFamily: "'Playfair Display',serif", fontSize: 19, color: "#fff", marginBottom: 12, whiteSpace: "pre-line", lineHeight: 1.2 }}>{title}</h2>
              <ul style={{ listStyle: "none", marginBottom: 20, display: "flex", flexDirection: "column", gap: 5 }}>
                {features.map(f => <li key={f} style={{ display: "flex", alignItems: "center", gap: 7, fontSize: 12, color: "rgba(255,255,255,.7)" }}><span style={{ fontSize: 11, color: SKY }}>✓</span>{f}</li>)}
              </ul>
              <div style={{ background: primary ? SKY : "#fff", color: NAVY, padding: "10px 16px", borderRadius: 9, fontSize: 13, fontWeight: 700, textAlign: "center" }}>{label}</div>
            </div>
          ))}
        </div>

        <div style={{ display: "flex", justifyContent: "center", gap: "clamp(16px,3vw,44px)", flexWrap: "wrap", paddingTop: 24, borderTop: "1px solid rgba(255,255,255,.07)", marginBottom: 28 }}>
          {[["Free", "For all job seekers"], ["24hr", "Consultant response"], ["100%", "Background verified"], ["Instant", "Employer access"], ["UK", "Nationwide"]].map(([n, l]) => (
            <div key={n} style={{ textAlign: "center" }}>
              <div style={{ fontFamily: "'Playfair Display',serif", fontSize: "clamp(18px,2.5vw,26px)", fontWeight: 900, color: SKY }}>{n}</div>
              <div style={{ fontSize: 10, color: "rgba(255,255,255,.38)", marginTop: 3 }}>{l}</div>
            </div>
          ))}
        </div>
        <p style={{ fontSize: 11, color: "rgba(255,255,255,.18)", paddingBottom: 28 }}>
          Path Charis Group · 07486 883 393 · www.pathcharis.co.uk · @pathcharis_uk
        </p>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────
// ROOT
// ─────────────────────────────────────────────────────────
export default function App() {
  const [view, setView] = useState("home");

  useEffect(() => {
    const style = document.createElement("style");
    style.textContent = CSS;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);

  return (
    <div style={{ minHeight: "100vh", background: "#F0F4F8" }}>
      {view === "home"     && <Landing onSeeker={() => setView("seeker")} onEmployer={() => setView("employer")} />}
      {view === "seeker"   && <div style={{ background: "#F0F4F8", minHeight: "100vh", paddingTop: 6 }}><SeekerApp onBack={() => setView("home")} /></div>}
      {view === "employer" && <div style={{ background: "#F0F4F8", minHeight: "100vh", paddingTop: 6 }}><EmployerApp onBack={() => setView("home")} /></div>}
    </div>
  );
}